import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub, usePrivate } from '../context';
import {
  bookmarks, dayKey, isBookmarked, loadActivities, loadHeat, loadPlayTime, loadPlayerHeat, loadRuns, loadSeries, MODES, PAGE_SIZE, pageForDate,
  REL_DAYS, REL_LONG_DAYS, seasonSpans, SERIES_KINDS, toggleBookmark, type ActivityRow, type Days, type PlayTime, type SeriesEntry,
} from '../activity';
import { clock } from './CollectionsTab';
import { hrs, StatTile, useFriends, useLegend, useRelationships } from '../shared';
import { Art, Asking, Bar, Card, Check, Counting, Draw, Heading, ListRow, Pic, SmallButton, Split } from '../ui';

/** Port of the app's Activity tab: Carnage, Heat, Hours, Bookmarks and Series. */
export default function ActivityTab({ sub }: { sub: string }) {
  switch (sub) {
    case 'Series': return <Series />;
    case 'Heat': return <Heat />;
    case 'Hours': return <Hours />;
    case 'Bookmarks': return <Bookmarks />;
    default: return <Carnage />;
  }
}

// ---------------- Carnage ----------------

/** One activity as a line: its kind, its name, when, and a star that keeps it in Bookmarks. */
export function ActivityLine({ r, onStar }: { r: ActivityRow; onStar?: () => void }) {
  const when = new Date(r.when);
  const age = Date.now() - when.getTime();
  const ago = age < 86_400_000 ? (age >= 3_600_000 ? `${Math.floor(age / 3_600_000)} hr, ${Math.floor((age % 3_600_000) / 60_000)} min ago` : `${Math.max(1, Math.floor(age / 60_000))} min ago`)
    : when.toLocaleDateString(undefined, { day: 'numeric', month: 'numeric', year: 'numeric' });
  const kept = isBookmarked(r.instanceId);
  return (
    <div className={`g-actline ${r.completed ? '' : 'failed'}`}
      title={`${r.kind}: ${r.name}\n${when.toLocaleString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}\n${clock(r.seconds)} played  |  ${Math.round(r.kills)} kills  |  ${Math.round(r.deaths)} deaths\n${r.completed ? 'Completed' : 'Not completed'}`}>
      <span className="kind">{r.kind}</span>
      <span className="name">{r.name}</span>
      <span className="when">{ago}</span>
      {r.instanceId && (
        <button type="button" className={`star ${kept ? 'on' : ''}`} title={kept ? 'Take this activity out of your bookmarks' : 'Keep this activity in your bookmarks'}
          onClick={() => { toggleBookmark(r); onStar?.(); }}>{kept ? '★' : '☆'}</button>
      )}
    </div>
  );
}

function Carnage() {
  const h = useHub();
  const char = h.char!;
  const [mode, setMode] = useState(0);
  const [page, setPage] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [seekError, setSeekError] = useState<string>();
  const [, bump] = useState(0);
  const { data: rows, error } = usePrivate(`acts:${char.id}:${mode}:${page}`, () => loadActivities(h.player!, char.id, mode, page), { public: true });
  const go = (p: number) => { setPage(p); window.scrollTo({ top: 0 }); };
  const done = rows?.filter((r) => r.completed).length ?? 0;
  return (
    <Split
      left={(
        <div className="g-stack" style={{ gap: 12 }}>
          <select className="g-input g-select big" value={mode} onChange={(e) => { setMode(Number(e.target.value)); setPage(0); }} aria-label="Activity mode">
            {MODES.map(([m, n]) => <option key={m} value={m}>{n}</option>)}
          </select>
          <p className="g-text2 big14">Post game carnage reports for the chosen activity mode: the culmination of all of your efforts.</p>
          <p className="g-dim small">Showing {char.cls}. Click your emblem in the header to switch character.</p>
          {rows && rows.length > 0 && <p className="g-dim small">On this page: {done} completed, {rows.length - done} not completed.</p>}
        </div>
      )}
      right={(
        <div className="g-stack" style={{ gap: 3 }}>
          {!rows ? <Asking text={seekError ?? error ?? 'Asking Bungie for your activities...'} />
            : rows.length === 0 ? <p className="g-text2">{page === 0 ? 'No activities of this kind for this character.' : 'No more activities.'}</p>
              : rows.map((r, i) => <ActivityLine key={r.instanceId || i} r={r} onStar={() => bump((x) => x + 1)} />)}
          <div className="g-paging">
            <SmallButton disabled={page === 0} onClick={() => go(0)}>Now</SmallButton>
            <SmallButton disabled={page === 0} onClick={() => go(page - 1)}>Previous</SmallButton>
            <SmallButton disabled={rows?.length !== PAGE_SIZE} onClick={() => go(page + 1)}>Next {PAGE_SIZE}</SmallButton>
            <input type="date" className="g-input g-date" max={dayKey(new Date())} disabled={seeking} aria-label="Jump to a day"
              onChange={async (e) => {
                if (!e.target.value) return;
                const [y, m, d] = e.target.value.split('-').map(Number);
                setSeeking(true); setSeekError(undefined);
                try { go(await pageForDate(h.player!, char.id, mode, new Date(y, m - 1, d))); } catch (err) { setSeekError(`That day could not be looked up (${err instanceof Error ? err.message : err}).`); } finally { setSeeking(false); }
              }} />
            {seeking && <span className="g-dim small">Looking for that day...</span>}
            <span className="g-dim small">Page {page + 1}</span>
          </div>
        </div>
      )}
    />
  );
}

function Bookmarks() {
  const [, bump] = useState(0);
  const all = bookmarks();
  return (
    <Split
      left={(
        <div className="g-stack" style={{ gap: 10 }}>
          <h1 className="g-title">Bookmarks</h1>
          <p className="g-text2 big14">A record of your victories, satisfaction, and dismay.</p>
          <p className="g-dim small">Every activity you starred on the Carnage page is kept here, in this browser. Click a star again to let one go.</p>
          {all.length > 0 && <p className="small">{all.length} kept, {all.filter((b) => b.completed).length} of them completed.</p>}
        </div>
      )}
      right={(
        <div className="g-stack" style={{ gap: 3 }}>
          {all.length === 0 ? <p className="g-text2">You have not added any bookmarks yet. Open Carnage (the first page on the left) and click the star at the end of an activity.</p>
            : all.map((b) => <ActivityLine key={b.instanceId} r={b} onStar={() => bump((x) => x + 1)} />)}
        </div>
      )}
    />
  );
}

// ---------------- Heat ----------------

const INKS: [string, string][] = [['#5f9bd8', '#8fc4ff'], ['#b3a1ff', '#d2a8ff'], ['#e0935f', '#ffb98a'], ['#4fc2b8', '#8ce8df'], ['#e06f9c', '#ff9cc0']];

function HeatCalendar({ heat, color, full, also, onPick, swipe }: { heat: Days; color: string; full: string; also?: Days; onPick?: (text: string) => void; swipe?: boolean }) {
  const cell = 19, gap = 3;
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => { if (swipe && scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth; }, [swipe]);   // open on the latest weeks
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(today); start.setDate(start.getDate() - 364);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));   // back to a Monday
  const squares: ReactNode[] = [];
  const months: ReactNode[] = [];
  let lastMonth = -1;
  for (let d = new Date(start), i = 0; d <= today; d.setDate(d.getDate() + 1), i++) {
    const week = Math.floor(i / 7), row = i % 7;
    if (d.getMonth() !== lastMonth && row === 0) {
      lastMonth = d.getMonth();
      months.push(<text key={`m${i}`} x={34 + week * (cell + gap)} y={11} className="hc-label">{d.toLocaleDateString(undefined, { month: 'short' })}</text>);
    }
    const k = dayKey(d);
    const v = heat[k];
    const hours = (v?.seconds ?? 0) / 3600;
    const fill = !v ? 'rgba(255,255,255,0.07)' : hours < 1 ? color + '4d' : hours < 2 ? color + '80' : hours < 4 ? color + 'bf' : full;
    const shared = !!v && !!also?.[k];
    const text = `${d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}\n${!v ? 'Nothing played' : `${v.count} activit${v.count === 1 ? 'y' : 'ies'}, ${Math.floor(v.seconds / 3600)}:${String(Math.floor((v.seconds % 3600) / 60)).padStart(2, '0')} played`}${shared ? '\nYou played that day too' : ''}`;
    squares.push(
      <rect key={k} x={34 + week * (cell + gap)} y={26 + row * (cell + gap)} width={cell} height={cell} rx={2.5} fill={fill} style={{ animationDelay: `${week * 9}ms` }} onClick={onPick && (() => onPick(text))}>
        <title>{text}</title>
      </rect>,
    );
  }
  const W = 34 + 54 * (cell + gap), H = 26 + 7 * (cell + gap);
  const svg = (
    <svg className="g-heat" viewBox={`0 0 ${W} ${H}`} style={swipe ? { width: Math.round(W * 0.72), maxWidth: 'none' } : { maxWidth: W }}>
      {months}
      {['Mon', '', 'Wed', '', 'Fri', '', 'Sun'].map((n, r) => n && <text key={n} x={0} y={26 + r * (cell + gap) + 14} className="hc-label">{n}</text>)}
      {squares}
    </svg>
  );
  return swipe ? <div className="g-heatscroll" ref={scroller}>{svg}</div> : svg;
}

const HeatKey = ({ color, full }: { color: string; full: string }) => (
  <div className="g-heatkey">
    <span>Less</span>
    {['rgba(255,255,255,0.07)', color + '4d', color + '80', color + 'bf', full].map((c) => <i key={c} style={{ background: c }} />)}
    <span>More  (under 1 h, 1 to 2 h, 2 to 4 h, over 4 h)</span>
  </div>
);

function Heat() {
  const h = useHub();
  const char = h.char!;
  const [all, setAll] = useState(false);
  const [relDays, setRelDays] = useState(REL_DAYS);
  const key = all ? 'all' : char.id;
  const { data: heat, error } = usePrivate(`heat:${key}`, () => (all ? loadPlayerHeat(h.player!.membershipId) : loadHeat(h.player!, char.id)), { public: true });
  const rel = useRelationships(relDays);
  const friends = useFriends();
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([]);
  const [theirs, setTheirs] = useState<Record<string, Days | string>>({});
  const [auto, setAuto] = useState(false);
  const [spans, setSpans] = useState<{ name: string; start: Date; end: Date }[]>([]);
  useEffect(() => { seasonSpans().then(setSpans).catch(() => setSpans([])); }, []);
  const toggle = (id: string, name: string) => {
    setPicked((p) => (p.some((x) => x.id === id) ? p.filter((x) => x.id !== id) : [...p, { id, name }]));
    if (!theirs[id]) loadPlayerHeat(id).then((d) => setTheirs((t) => ({ ...t, [id]: d })))
      .catch((e) => setTheirs((t) => ({ ...t, [id]: `Bungie did not give out this player's year: ${e instanceof Error ? e.message : e}` })));
  };
  // the first time the lists are in, the comparison opens by itself: the friend most often in your fireteam
  useEffect(() => {
    if (auto || picked.length || !rel.data || (h.own && !friends.data && !friends.error)) return;
    setAuto(true);
    const mates = rel.data.people.filter((p) => p.fireteams > 0 && p.name).sort((a, b) => b.fireteams - a.fireteams || b.activities - a.activities);
    const top = mates.find((m) => friends.data?.some((f) => f.memberId === m.memberId || f.name.toLowerCase() === m.name.toLowerCase())) ?? mates[0];
    if (top) toggle(top.memberId, top.name);
  }, [rel.data, friends.data, friends.error]);   // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    if (!heat) return null;
    const entries = Object.entries(heat);
    const busiest = entries.sort((a, b) => b[1].seconds - a[1].seconds)[0];
    const per = spans.map((s) => ({ name: s.name, start: s.start, seconds: entries.filter(([k]) => { const d = new Date(k); return d >= s.start && d < s.end; }).reduce((t, [, v]) => t + v.seconds, 0) }))
      .filter((s) => s.seconds > 0).sort((a, b) => b.start.getTime() - a.start.getTime());
    return { days: entries.length, seconds: entries.reduce((t, [, v]) => t + v.seconds, 0), count: entries.reduce((t, [, v]) => t + v.count, 0), busiest, per };
  }, [heat, spans]);
  const phone = usePhone();
  const [day, setDay] = useState<string>();
  const [lists, setLists] = useState(false);
  const together = (memberId: string, name: string) => rel.data?.people.find((p) => p.memberId === memberId || p.name.toLowerCase() === name.toLowerCase())?.fireteams ?? 0;

  const intro = (
    <>
      <h1 className="g-title">Heat</h1>
      <p className="g-text2 big14">The past year, one square per day. The brighter the square, the longer you played that day.{phone ? ' Tap a square to see that day; swipe the year sideways.' : ''}</p>
      <Check label="All characters" on={all} set={setAll} />
      <p className="g-dim small">{all ? 'Showing all your characters together.' : `Showing ${char.cls}. ${phone ? 'Tap' : 'Click'} your emblem in the header to switch character.`}</p>
    </>
  );
  const stats = totals && totals.days > 0 && (
    <div className={phone ? 'g-heatstats' : 'g-stack'} style={phone ? undefined : { gap: 12 }}>
      <Big value={totals.days} label="Days played" />
      <Big value={hrs(totals.seconds)} label="Time in activities" />
      <Big value={totals.count} label="Activities" />
      <Big value={new Date(totals.busiest[0]).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} label={`Busiest day (${(totals.busiest[1].seconds / 3600).toFixed(1)} h)`} />
    </div>
  );
  const seasons = totals && totals.per.length > 0 && (
    <>
      <Heading style={{ marginTop: 14 }}>B Y   S E A S O N</Heading>
      {totals.per.map((x) => (
        <div key={x.name} className="g-stack" style={{ gap: 4 }}>
          <div className="g-twocol"><span>{x.name}</span><span className="g-text2">{hrs(x.seconds)}</span></div>
          <Bar fraction={x.seconds / Math.max(...totals.per.map((y) => y.seconds))} color="#7bd88a" height={3} />
        </div>
      ))}
    </>
  );
  const mates = rel.data ? rel.data.people.filter((p) => p.fireteams > 0 && p.name).sort((a, b) => b.fireteams - a.fireteams || b.activities - a.activities).slice(0, 12) : [];
  const compare = (
    <>
      <Heading style={{ marginTop: 18 }}>C O M P A R E   W I T H</Heading>
      {picked.length > 0 && <div><SmallButton onClick={() => setPicked([])}>Show only my year</SmallButton></div>}
      {phone && !lists ? (
        <>
          <p className="g-dim small">Lay a friend's year under yours: the people you play with most and your Bungie friends.</p>
          <div><SmallButton onClick={() => setLists(true)}>Pick who to compare with</SmallButton></div>
        </>
      ) : (
        <>
          <div className="g-caps-label" style={{ marginTop: 4 }}>PLAYED WITH MOST</div>
          {!rel.data ? <p className="g-text2 small">{rel.error ?? `Reading your match reports to see who was in your fireteam...${rel.progress[1] ? `  ${rel.progress[0]} / ${rel.progress[1]}` : ''}`}</p> : (
            <>
              <p className="g-dim small">{rel.data.people.some((p) => p.fireteams > 0) ? `Fireteams together in the last ${rel.data.days} days.` : `No fireteam found in your last ${rel.data.days} days.`}</p>
              {mates.map((m) => <ListRow key={m.memberId} name={m.name} count={String(m.fireteams)} selected={picked.some((p) => p.id === m.memberId)} onClick={() => toggle(m.memberId, m.name)} />)}
              <div><SmallButton onClick={() => setRelDays(rel.data!.days > REL_DAYS ? REL_DAYS : REL_LONG_DAYS)}>{rel.data.days > REL_DAYS ? 'Last 90 days' : 'Look back a whole year'}</SmallButton></div>
            </>
          )}
          <div className="g-caps-label" style={{ marginTop: 12 }}>BUNGIE FRIENDS</div>
          {!h.own ? <p className="g-text2 small">Sign in with Bungie (top right) to list your Bungie friends.</p>
            : !friends.data ? <Asking text={friends.error ? `Bungie did not give out your friends list: ${friends.error}` : 'Asking Bungie for your friends list...'} />
              : friends.data.length === 0 ? <p className="g-text2 small">No Bungie friends with a Destiny account were found.</p>
                : (
                  <>
                    {[...friends.data].sort((a, b) => together(b.memberId, b.name) - together(a.memberId, a.name)).slice(0, phone ? 25 : 60).map((f) => {
                      const n = together(f.memberId, f.name);
                      return <ListRow key={f.memberId} name={f.name} count={n > 0 ? `${n}${f.online ? '  online' : ''}` : f.online ? 'online' : ''} selected={picked.some((p) => p.id === f.memberId)} onClick={() => toggle(f.memberId, f.name)} />;
                    })}
                    {friends.data.length > (phone ? 25 : 60) && <p className="g-dim small">and {friends.data.length - (phone ? 25 : 60)} more</p>}
                  </>
                )}
          {phone && <div><SmallButton onClick={() => setLists(false)}>Fold the lists away</SmallButton></div>}
        </>
      )}
    </>
  );
  const calendars = !heat ? <Asking text={error ?? 'Asking Bungie for a year of activities...'} /> : (
    <div className="g-stack" style={{ gap: 14 }}>
      <HeatCalendar heat={heat} color="#5fa86b" full="#7bd88a" onPick={setDay} swipe={phone} />
      <HeatKey color="#5fa86b" full="#7bd88a" />
      {day && <p className="g-heatday">{day}</p>}
      {Object.keys(heat).length === 0 && <p className="g-text2">Nothing played on this character in the past year.</p>}
      {picked.map((f, n) => {
        const t = theirs[f.id];
        const [c, full] = INKS[n % INKS.length];
        return (
          <div key={f.id} className="g-stack" style={{ gap: 14 }}>
            <Heading style={{ marginTop: 22 }}>{f.name.toUpperCase().split('').join(' ')}</Heading>
            {!t || typeof t === 'string' ? <Asking text={typeof t === 'string' ? t : `Asking Bungie for ${f.name}'s year (every character)...`} /> : (
              <>
                <p className="g-text2" style={{ fontSize: 13 }}>
                  {Object.keys(t).length === 0 ? 'Nothing played in the past year.'
                    : `${Object.keys(t).length.toLocaleString()} days played, ${hrs(Object.values(t).reduce((x, v) => x + v.seconds, 0))} in activities. You both played on ${Object.keys(heat).filter((k) => t[k]).length.toLocaleString()} days; your squares above are this character only.`}
                </p>
                <HeatCalendar heat={t} color={c} full={full} also={heat} onPick={setDay} swipe={phone} />
                <HeatKey color={c} full={full} />
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  // a phone: one column, the year near the top (it was under every list, thousands of pixels down)
  if (phone) return <div className="g-stack" style={{ gap: 12 }}>{intro}{stats}{calendars}{seasons}{compare}</div>;
  return <Split left={<div className="g-stack" style={{ gap: 12 }}>{intro}{stats}{seasons}{compare}</div>} right={calendars} />;
}

/** True on a phone-sized screen (the site's 600 px breakpoint), following the window as it changes. */
function usePhone() {
  const q = '(max-width: 600px)';
  const [on, setOn] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q), set = () => setOn(m.matches);
    m.addEventListener('change', set);
    return () => m.removeEventListener('change', set);
  }, []);
  return on;
}

const Big = ({ value, label }: { value: number | string; label: string }) => (
  <div style={{ marginTop: 6 }}>
    <div style={{ fontSize: 20 }}>{typeof value === 'number' ? <Counting value={value} /> : value}</div>
    <div className="g-dim small">{label}</div>
  </div>
);

// ---------------- Hours ----------------

function Hours() {
  const { error } = useLegend();
  return (
    <>
      <h1 className="g-title">Hours</h1>
      <p className="g-text2 big14">Where your time in Destiny 2 went, every character together. Click PvE, Crucible or Custom games to see those activities.</p>
      <HoursBlock />
      {error && <Asking text={error} />}
    </>
  );
}

export function HoursBlock() {
  const h = useHub();
  const nav = useNavigate();
  const { data: legend } = useLegend();
  const rel = useRelationships();
  const friends = useFriends();
  const [friend, setFriend] = useState<{ id: string; name: string }>();
  const [times, setTimes] = useState<Record<string, PlayTime | string>>({});
  const [menu, setMenu] = useState(false);
  const inGame = h.hub!.chars.reduce((s, c) => s + c.minutes, 0) * 60;
  const tiles: ReactNode[] = [];
  const split: { name: string; seconds: number; color: string }[] = [];
  tiles.push(<StatTile key="total" label="Total time played" value={hrs(inGame)} color="var(--gold)" sub="all characters together" wide title="The time the game itself counts for your characters, from logging in to logging out." />);
  if (legend) {
    const pve = legend.pve.secondsPlayed?.value ?? 0, pvp = legend.pvp.secondsPlayed?.value ?? 0, priv = legend.priv.secondsPlayed?.value ?? 0;
    const rest = inGame - pve - pvp - priv;
    const pct = (s: number) => (inGame > 0 ? `${Math.round((s * 100) / inGame)}% of your time` : '');
    tiles.push(<button key="pve" type="button" className="g-tilebtn" onClick={() => nav('/guardian/activity')}><StatTile label="PvE" value={hrs(pve)} sub={pct(pve)} wide title="Time inside PvE activities: strikes, raids, dungeons, story, patrol and the rest (Bungie's all-time PvE total)." /></button>);
    tiles.push(<StatTile key="pvp" label="Crucible (online PvP)" value={hrs(pvp)} color="var(--bad)" sub={pct(pvp)} wide title="Time inside matchmade Crucible matches (Bungie's all-time PvP total; private matches are counted apart)." />);
    if (priv > 0) tiles.push(
      <button type="button" key="custom" className="g-custom" onClick={() => nav('/guardian/activity/d2-tracker?mode=private')}
        title={`Time in custom games (private matches), all characters, all time.\n${(priv / 3600).toFixed(1)} hours over ${(legend.priv.activitiesEntered?.value ?? 0).toLocaleString()} matches\n\nClick for your private matches in the D2 Tracker`}>
        <div className="g-stattile-label" style={{ color: '#f2ecff' }}>CUSTOM GAMES</div>
        <div className="g-stattile-value"><Counting value={Math.floor(priv / 3600)} format={(n) => `${n.toLocaleString()} h`} /></div>
        <div className="small" style={{ color: '#e6dcff' }}>{(legend.priv.activitiesEntered?.value ?? 0).toLocaleString()} private matches</div>
      </button>,
    );
    if (rest > 0) tiles.push(<StatTile key="rest" label="Everywhere else" value={hrs(rest)} color="var(--info)" sub={pct(rest)} wide title="What is left of your in-game time after PvE, Crucible and custom games: orbit, the Tower and other social spaces, menus, loading and matchmaking. Worked out by subtraction." />);
    split.push({ name: 'PvE', seconds: pve, color: '#e8c36b' }, { name: 'Crucible', seconds: pvp, color: 'var(--bad)' }, { name: 'Custom games', seconds: priv, color: 'var(--accent-text)' }, { name: 'Everywhere else', seconds: Math.max(0, rest), color: 'var(--info)' });
  }
  const parts = split.filter((s) => s.seconds > 0);
  const whole = parts.reduce((s, p) => s + p.seconds, 0);
  const pick = (id: string, name: string) => {
    setMenu(false); setFriend({ id, name });
    if (!times[id]) loadPlayTime(id).then((t) => setTimes((x) => ({ ...x, [id]: t }))).catch((e) => setTimes((x) => ({ ...x, [id]: `Their hours could not be read (${e instanceof Error ? e.message : e}).` })));
  };
  const mates = rel.data?.people.filter((p) => p.fireteams > 0 && p.name).sort((a, b) => b.fireteams - a.fireteams).slice(0, 12) ?? [];
  const others = friends.data?.filter((f) => mates.every((m) => m.memberId !== f.memberId)).slice(0, 60) ?? [];
  const t = friend ? times[friend.id] : undefined;
  return (
    <>
      <div className="g-twocol" style={{ marginTop: 22, alignItems: 'flex-end' }}>
        <div className="g-heading" style={{ margin: 0 }}>T I M E   P L A Y E D</div>
        {legend && (
          <div style={{ position: 'relative' }}>
            <button type="button" className="btn primary" onClick={(e) => { e.stopPropagation(); setMenu(!menu); }}>{friend ? 'Compare with someone else' : 'Compare with a friend'}</button>
            {menu && (
              <div className="g-menu g-pickmenu" onClick={(e) => e.stopPropagation()}>
                {mates.length > 0 && <div className="g-caps-label">PLAYED WITH MOST</div>}
                {mates.map((m) => <ListRow key={m.memberId} name={m.name} count={String(m.fireteams)} selected={friend?.id === m.memberId} onClick={() => pick(m.memberId, m.name)} />)}
                {others.length > 0 && <div className="g-caps-label" style={{ marginTop: 8 }}>BUNGIE FRIENDS</div>}
                {others.map((f) => <ListRow key={f.memberId} name={f.name} count={f.online ? 'online' : ''} selected={friend?.id === f.memberId} onClick={() => pick(f.memberId, f.name)} />)}
                {!mates.length && !others.length && <p className="g-text2">{!rel.data ? 'Reading your match reports to see who you play with. Open this again in a moment.' : 'Nobody to list yet. Sign in with Bungie (top right) for your friends list.'}</p>}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="g-timetiles" style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}>{tiles}</div>
      {parts.length > 1 && (
        <>
          <div className="g-splitbar">{parts.map((p) => <span key={p.name} title={`${p.name}: ${hrs(p.seconds)} (${Math.round((p.seconds * 100) / whole)}%)`} style={{ flex: p.seconds, background: `linear-gradient(90deg, color-mix(in srgb, ${p.color} 58%, black), ${p.color})` }} />)}</div>
          <div className="g-splitkey">{parts.map((p) => <span key={p.name}><i style={{ background: p.color }} />{p.name}  {Math.round((p.seconds * 100) / whole)}%</span>)}</div>
        </>
      )}
      {friend && legend && (
        <Card style={{ padding: 18 }}>
          <div className="g-stack" style={{ gap: 10 }}>
            <div className="g-compare-key">
              <span><i style={{ background: 'var(--accent-text)' }} />You</span>
              <span><i style={{ background: '#5fb0e8' }} />{friend.name}</span>
              <SmallButton onClick={() => setFriend(undefined)}>Hide</SmallButton>
            </div>
            {!t || typeof t === 'string' ? <Asking text={typeof t === 'string' ? t : `Asking Bungie for ${friend.name}'s hours...`} /> : (() => {
              const pve = legend.pve.secondsPlayed?.value ?? 0, pvp = legend.pvp.secondsPlayed?.value ?? 0, priv = legend.priv.secondsPlayed?.value ?? 0;
              const rows: [string, number, number][] = [['In game', inGame, t.total], ['PvE', pve, t.pve], ['Crucible', pvp, t.pvp], ['Custom games', priv, t.priv], ['Everywhere else', Math.max(0, inGame - pve - pvp - priv), Math.max(0, t.total - t.pve - t.pvp - t.priv)]];
              const most = Math.max(1, ...rows.map((r) => Math.max(r[1], r[2])));
              return rows.map(([label, mine, theirs]) => (
                <div key={label} className="g-comparerow">
                  <span>{label}</span>
                  <div className="g-stack" style={{ gap: 3 }}>
                    {[[mine, 'var(--accent-text)'], [theirs, '#5fb0e8']].map(([v, c], i) => (
                      <div key={i} className="g-comparebar"><Bar fraction={(v as number) / most} color={c as string} height={10} /><span className="g-text2 small">{hrs(v as number)}</span></div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </Card>
      )}
    </>
  );
}

// ---------------- Series ----------------

function Series() {
  const h = useHub();
  const nav = useNavigate();
  const char = h.char!;
  const [kind, setKind] = useState('Raid');
  const [open, setOpen] = useState<SeriesEntry>();
  const { data: series, error } = usePrivate(`series:${char.id}`, () => loadSeries(h.player!, char.id), { public: true });
  if (open) return <SeriesDetail e={open} kind={kind} back={() => setOpen(undefined)} />;
  const list = series?.[kind] ?? [];
  return (
    <Split
      left={(
        <div className="g-stack" style={{ gap: 12 }}>
          <select className="g-input g-select big" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind of activity">
            {SERIES_KINDS.map((k) => <option key={k} value={k}>{k}s</option>)}
          </select>
          <p className="g-text2 big14">Every activity of this kind you have set foot in, with how often you cleared it and your best time.</p>
          <p className="g-dim small">Showing {char.cls}. Click your emblem in the header to switch character.</p>
          {list.length > 0 && (
            <div className="g-cols three" style={{ marginTop: 8 }}>
              <Big value={list.reduce((s, e) => s + e.clears, 0)} label="Cleared" />
              <Big value={Math.round(list.reduce((s, e) => s + e.kills, 0))} label="Kills" />
              <Big value={hrs(list.reduce((s, e) => s + e.seconds, 0))} label="Time played" />
            </div>
          )}
        </div>
      )}
      right={!series ? <Asking text={error ?? 'Asking Bungie for your activity totals...'} /> : list.length === 0 ? <p className="g-text2">Nothing of this kind played on this character yet.</p> : (
        <div className="g-wrap" style={{ gap: 4 }}>
          {kind === 'Raid' && (
            <button type="button" className="g-seriestile planner" title="Open the Raid Planner" onClick={() => nav('/guardian/maps?planner=1')}>
              <Art d={Draw.Planner} size={54} stroke={1.2} />
              <span className="words"><b>Raid Planner</b><span>Plan an encounter on its map: who stands where and who does what.</span></span>
            </button>
          )}
          {list.map((e) => (
            <button key={e.name} type="button" className="g-seriestile" onClick={() => { setOpen(e); window.scrollTo({ top: 0 }); }}
              title={`${e.name}\n${e.clears.toLocaleString()} clears  |  best ${clock(e.best)}\n${Math.round(e.kills).toLocaleString()} kills  |  ${Math.round(e.deaths).toLocaleString()} deaths  |  ${(e.seconds / 3600).toFixed(1)} hours\n\nClick for every run`}>
              <Pic src={e.image} className="art" style={e.clears > 0 ? undefined : { opacity: 0.45 }} lazy />
              <b className="name">{e.name}</b>
              <span className="foot"><span><small>Cleared</small><b>{e.clears.toLocaleString()}</b></span><span><small>Fastest</small><b>{clock(e.best)}</b></span></span>
            </button>
          ))}
        </div>
      )}
    />
  );
}

function SeriesDetail({ e, kind, back }: { e: SeriesEntry; kind: string; back: () => void }) {
  const h = useHub();
  const char = h.char!;
  const { data: runs, error } = usePrivate(`runs:${char.id}:${e.name}`, () => loadRuns(h.player!, char.id, kind, e.hashes), { public: true });
  const line = (label: string, value: string) => <div key={label} className="g-statrow"><span /><span>{label}</span><b>{value}</b></div>;
  return (
    <Split
      left={(
        <div className="g-stack" style={{ gap: 10 }}>
          <div><SmallButton onClick={back}>Back to {kind}s</SmallButton></div>
          <Pic src={e.image} className="g-seriesart" />
          <h2 className="g-booktitle" style={{ fontSize: 24 }}>{e.name}</h2>
          {line('Cleared', e.clears.toLocaleString())}
          {line('Fastest clear', clock(e.best))}
          {line('Kills', Math.round(e.kills).toLocaleString())}
          {line('Deaths', Math.round(e.deaths).toLocaleString())}
          {line('Time played', `${(e.seconds / 3600).toFixed(1)} h`)}
          <p className="g-dim small">Totals are for your {char.cls}, all difficulties together.</p>
        </div>
      )}
      right={!runs ? <Asking text={error ?? 'Asking Bungie for every run...'} /> : runs.length === 0 ? <p className="g-text2">No runs found in your recent history (the list reaches back 1,000 activities of this kind).</p> : (
        <div className="g-stack" style={{ gap: 3 }}>
          <div className="g-runrow head"><span /><span>WHEN</span><span>RESULT</span><span>TIME</span><span>KILLS</span><span>DEATHS</span></div>
          {runs.map((r, i) => (
            <div key={r.instanceId || i} className={`g-runrow ${r.completed ? '' : 'failed'}`}>
              <span />
              <span className="when">{new Date(r.when).toLocaleString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              <span className="result">{r.completed ? 'Completed' : 'Not completed'}</span>
              <span>{clock(r.seconds)}</span><span>{Math.round(r.kills).toLocaleString()}</span><span>{Math.round(r.deaths).toLocaleString()}</span>
            </div>
          ))}
          <p className="g-dim small" style={{ marginTop: 10 }}>{runs.length} runs listed, {runs.filter((r) => r.completed).length} completed.</p>
        </div>
      )}
    />
  );
}

